import { EntryType } from '@shared/modules/entry/enums/entry-type.enum';
import { Attachment } from '@shared/interfaces/attachment.interface';
import { Entry } from '@shared/interfaces/entry.interface';
import { extensionIcon } from '@shared/helpers/extension-icon.helper';
import { extensionHelper } from '@shared/helpers/extension.helper';
import { ProjectSummaryDocumentsSectionsConfig } from '@modules/projects/shared/consts/project-summary-documents-sections.const';

export interface ProjectSummaryDocumentSectionView {
  translation: string;
  type: EntryType;
  items: ProjectSummaryDocumentItemView[];
  infoText?: string;
  translationParams?: Record<string, string>;
}

export interface ProjectSummaryDocumentGroupView {
  boxName: 'dataBox2' | 'dataBox3';
  title: string;
  description: string;
  sections: ProjectSummaryDocumentSectionView[];
  hasDocuments: boolean;
}

export interface ProjectSummaryDocumentItemView {
  id: number;
  name: string;
  created: string;
  description: string;
  descriptionText: string;
  previewAttachments: ProjectSummaryDocumentAttachmentView[];
  entry: Entry;
}

export interface ProjectSummaryDocumentAttachmentView extends Attachment {
  isImage: boolean;
  isPdf: boolean;
  icon: string;
  customType: string;
}

export function mapProjectSummaryEntryToItemView(entry: Entry): ProjectSummaryDocumentItemView {
  const previewAttachments = [...entry.attachments]
    .filter((attachment) => !attachment.deleted)
    .sort(compareProjectSummaryAttachments)
    .map((attachment) => mapProjectSummaryAttachmentToView(attachment));

  const description = linkifyProjectSummaryDescription(entry.description || '');

  return {
    id: entry.id,
    name: entry.name || '',
    created: entry.created,
    description,
    descriptionText: stripProjectSummaryHtml(description),
    previewAttachments,
    entry
  };
}

export function mapProjectSummaryAttachmentToView(attachment: Attachment): ProjectSummaryDocumentAttachmentView {
  const mappedAttachment = new Attachment(attachment) as ProjectSummaryDocumentAttachmentView;
  mappedAttachment.isImage = mappedAttachment.type?.indexOf('image') > -1;
  mappedAttachment.isPdf = mappedAttachment.type?.indexOf('pdf') > -1;
  mappedAttachment.icon = extensionIcon(mappedAttachment.type, mappedAttachment.file);
  mappedAttachment.customType = mappedAttachment.isImage
    ? getProjectSummaryImageExtension(mappedAttachment)
    : extensionHelper(mappedAttachment.type, mappedAttachment.file);
  return mappedAttachment;
}

export function getProjectSummaryEntries(entries: Entry[]): Entry[] {
  return (entries || [])
    .filter((entry) => !entry?.deleted)
    .map((entry) => new Entry(entry))
    .sort(compareProjectSummaryEntries);
}

export function getProjectSummarySectionTranslationParams(
  type: EntryType,
  companyName?: string | null
): Record<string, string> | undefined {
  return getProjectSummaryBoxNameForEntryType(type) === 'dataBox3' ? { name: companyName || '' } : undefined;
}

export function getProjectSummaryBoxNameForEntryType(type: EntryType): 'dataBox2' | 'dataBox3' {
  return ProjectSummaryDocumentsSectionsConfig.some(
    (group) => group.boxName === 'dataBox3' && group.sections.some((section) => section.type === type)
  )
    ? 'dataBox3'
    : 'dataBox2';
}

export function getProjectSummaryAllowedDocumentTypes(boxName: 'dataBox2' | 'dataBox3'): EntryType[] {
  return (
    ProjectSummaryDocumentsSectionsConfig.find((group) => group.boxName === boxName)?.sections.map(
      (section) => section.type
    ) || []
  );
}

export function getProjectSummaryAllAllowedDocumentTypes(): EntryType[] {
  const allTypes = ProjectSummaryDocumentsSectionsConfig.reduce<EntryType[]>((acc, group) => {
    group.sections.forEach((section) => acc.push(section.type));
    return acc;
  }, []);

  return Array.from(
    new Set(allTypes)
  );
}

export function updateProjectSummaryEntryAttachments(
  entries: Entry[],
  entryId: number,
  updateAttachment: (attachment: Attachment) => Attachment | null
): Entry[] {
  return (entries || []).map((entry) => {
    if (entry.id !== entryId) return entry;

    const nextEntry = new Entry(entry);
    nextEntry.attachments = (entry.attachments || [])
      .map((attachment) => updateAttachment(attachment))
      .filter((attachment): attachment is Attachment => !!attachment);
    nextEntry.imgAttachments = nextEntry.attachments.filter((attachment) => attachment.type.indexOf('image') > -1);
    nextEntry.fileAttachments = nextEntry.attachments.filter((attachment) => attachment.type.indexOf('image') === -1);
    return nextEntry;
  });
}

export function stripProjectSummaryHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function linkifyProjectSummaryDescription(value: string): string {
  if (!value || value.includes('<a ')) return value;

  return value.replace(
    /((?:https?:\/\/|www\.)[^\s<]+)/gi,
    (rawUrl) => {
      const normalizedUrl = rawUrl.startsWith('www.') ? `https://${rawUrl}` : rawUrl;
      return `<a href="${normalizedUrl}" target="_blank" rel="noopener noreferrer">${rawUrl}</a>`;
    }
  );
}

export function getProjectSummaryCreatedTimestamp(created: string | null | undefined): number {
  return created ? new Date(created).getTime() || 0 : 0;
}

function compareProjectSummaryEntries(first: Entry, second: Entry): number {
  const createdDiff = getProjectSummaryCreatedTimestamp(second.created) - getProjectSummaryCreatedTimestamp(first.created);
  if (createdDiff !== 0) return createdDiff;

  return Number(second.id || 0) - Number(first.id || 0);
}

function compareProjectSummaryAttachments(first: Attachment, second: Attachment): number {
  const createdDiff = getProjectSummaryCreatedTimestamp(second.created) - getProjectSummaryCreatedTimestamp(first.created);
  if (createdDiff !== 0) return createdDiff;

  const idDiff = Number(second.id || 0) - Number(first.id || 0);
  if (idDiff !== 0) return idDiff;

  const fileDiff = String(first.file || '').localeCompare(String(second.file || ''));
  if (fileDiff !== 0) return fileDiff;

  return String(first.name || '').localeCompare(String(second.name || ''));
}

function getProjectSummaryImageExtension(attachment: Attachment): string {
  const nameExtension = getProjectSummaryFileExtension(attachment.name);
  if (nameExtension) return nameExtension;

  const fileExtension = getProjectSummaryFileExtension(attachment.file);
  if (fileExtension) return fileExtension;

  const mimeExtension = attachment.type?.split('/')[1];
  return mimeExtension ? mimeExtension.toUpperCase() : 'IMAGE';
}

function getProjectSummaryFileExtension(value?: string | null): string {
  if (!value) return '';

  const normalizedValue = value.split('?')[0];
  const segments = normalizedValue.split('.');
  if (segments.length < 2) return '';

  return segments[segments.length - 1].toUpperCase();
}
